import React from "react";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { ExternalLink } from "../../components/ExternalLink";
import LoadingPage from "../../components/LoadingPage";
import Authorization from "./Authorization";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { OrganisationProvider } from "../../context/OrganisationContext";
import { useAppInsights } from "../../services/appInsights";
import { TelemetryEvents } from "../../telemetry/eventNames";
import { TelemetryProp, toTelemetryProps } from "../../telemetry/telemetryProps";
import { TelemetrySurface } from "../../telemetry/surfaces";
import { getPlatform, openDialog } from "../../taskpane";
import { invariant } from "../../utils/invariant";
import { withPromiseResolvers } from "../../utils/withPromiseResolvers";

const Authentication: React.FC<{ children }> = ({ children }) => {
  const { trackEvent } = useAppInsights();
  const { loginWithPopup, loginWithRedirect, handleRedirectCallback, isLoading, isAuthenticated } = useAuth0();

  const clearSuggestedChasableFiltersFromLocalStorage = () => {
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith("attelas-suggestedChasableFilters-"))
        .forEach((key) => localStorage.removeItem(key));
    } catch (e) {
      console.error(e);
    }
  };

  const signIn = async () => {
    try {
      if (getPlatform() === Office.PlatformType.Mac) {
        await loginWithRedirect({
          async openUrl(url) {
            try {
              const dialog = await openDialog(url);

              invariant(!!dialog, "Unable to open dialog");

              const { promise, resolve, reject } = withPromiseResolvers<string>();
              dialog.addEventHandler(Office.EventType.DialogMessageReceived, (event) => {
                if ("error" in event) {
                  reject(event.error);
                } else {
                  resolve(event.message);
                }
                dialog.close();
              });

              const redirectUri = await promise;
              await handleRedirectCallback(redirectUri);
            } catch (e) {
              console.error(e);
            }
          },
        });
      } else {
        await loginWithPopup();
      }
      clearSuggestedChasableFiltersFromLocalStorage();
      trackEvent({
        name: TelemetryEvents.userLoggedIn,
        properties: toTelemetryProps({ [TelemetryProp.surface]: TelemetrySurface.authentication }),
      });
    } catch (error) {
      console.error(error);
      trackEvent({
        name: TelemetryEvents.userLoggedInFailed,
        properties: toTelemetryProps({
          [TelemetryProp.surface]: TelemetrySurface.authentication,
          [TelemetryProp.error]: error instanceof Error ? error.message : String(error),
        }),
      });
    }
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <>
      {isAuthenticated ? (
        <OrganisationProvider>
          <Authorization>{children}</Authorization>
        </OrganisationProvider>
      ) : (
        <>
          <Stack justifyContent="space-between" height="100%">
            <Grid container={true} justifyContent="space-between" alignItems="center" mb={2} p={1}>
              <Grid size={12} textAlign="center">
                <Typography component="h1" variant="subtitle1">
                  Welcome
                </Typography>
              </Grid>
            </Grid>
            <Grid container={true} justifyContent="space-between" alignItems="center" mb={2} p={1}>
              <Grid size={12} textAlign="center">
                <Typography component="h1" variant="body1" mb={2} fontSize={16}>
                  Attelas is a built-in Accounts Receivable Assistant for finance teams
                </Typography>
                <Button onClick={signIn} fullWidth variant="outlined">
                  Sign In
                </Button>
              </Grid>
            </Grid>
            <Grid container={true} justifyContent="space-between" alignItems="center" mb={2} p={1}>
              <Grid size={12} textAlign="center">
                <Typography component="p" variant="body1">
                  Trouble logging in?
                </Typography>
                <Typography component="p" variant="body1">
                  <ExternalLink href="https://attelas.ai/support">https://attelas.ai/support</ExternalLink>
                  <br />
                  <Link to="mailto:hello@attelas.ai">hello@attelas.ai</Link>
                  <br />
                  <Link to="tel:+442032906245">+44 (0) 20 3290 6245</Link>
                </Typography>
              </Grid>
            </Grid>
          </Stack>
        </>
      )}
    </>
  );
};

export default Authentication;
